import React from 'react';
import { Alert, Box, Grid, TextField } from '@mui/material';
import { GiftProduct, GiftProductType } from '../../../../models';
import { CASHBACK_REWARD_IMAGE_URL } from '../../../../constants';
import { CurrencyInput } from '../../../../components/mui/inputs';

interface CashbackProductFormProps {
  product: Partial<GiftProduct>;
  onChange: (product: Partial<GiftProduct>) => void;
}

const formatBrl = (value?: number | null) =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const defaultCashbackName = (value?: number | null) => `Cashback de ${formatBrl(value)}`;

export const defaultCashbackDescription = (value?: number | null) =>
  `Vale ${formatBrl(value)} de desconto na sua próxima compra.`;

/**
 * Recompensa em dinheiro: é um produto virtual com valor de face, não um tipo novo.
 *
 * O cupom sai com o par (is_cashback, partner_value) que o PDV já aplica e o app já
 * desenha como cashback — por isso aqui só se pede o valor. Nome e descrição vêm
 * prontos do valor e seguem reescrevendo enquanto o operador não editar à mão.
 */
const CashbackProductForm: React.FC<CashbackProductFormProps> = ({ product, onChange }) => {
  const handleValueChange = (cashValue: number) => {
    const previousValue = product.cashValue ?? null;
    const nameIsDefault = !product.productName || product.productName === defaultCashbackName(previousValue);
    const descriptionIsDefault = !product.productDescription
      || product.productDescription === defaultCashbackDescription(previousValue);

    onChange({
      ...product,
      type: GiftProductType.Virtual,
      cashValue,
      productName: nameIsDefault ? defaultCashbackName(cashValue) : product.productName,
      productDescription: descriptionIsDefault
        ? defaultCashbackDescription(cashValue)
        : product.productDescription,
      productImagePath: product.productImagePath || CASHBACK_REWARD_IMAGE_URL || undefined,
      // A outra mecânica do produto virtual (desconto percentual em SKUs) não convive
      // com o valor de face: o backend e o CHECK do banco recusam os dois juntos.
      saleProductIds: [],
      discountPercentage: undefined,
      integrationType: null,
    });
  };

  const handleChange = (field: 'productName' | 'productDescription', value: string) => {
    onChange({ ...product, type: GiftProductType.Virtual, [field]: value });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <CurrencyInput
            fullWidth
            label="Valor do Cashback"
            value={product.cashValue ?? undefined}
            onChange={handleValueChange}
            required
            helperText="Valor em reais que o cliente recebe ao conquistar. É o desconto que o PDV aplica na compra."
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Nome"
            value={product.productName || ''}
            onChange={(e) => handleChange('productName', e.target.value)}
            required
            helperText="Aparece na vitrine do Clube e na notificação de conquista."
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Descrição"
            value={product.productDescription || ''}
            onChange={(e) => handleChange('productDescription', e.target.value)}
          />
        </Grid>
        <Grid size={12}>
          <Alert severity="info">
            O cupom é resgatável em qualquer local da rede que aceite cashback — não há locais de
            troca a escolher — e o app mostra o valor em reais no lugar da imagem. O desconto
            negociado de cada local continua valendo no faturamento.
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CashbackProductForm;
